import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import { DesktopNav } from '@/components/layout/DesktopNav'
import { MobileNav } from '@/components/layout/MobileNav'
import { SiteFooter } from '@/components/layout/SiteFooter'
import { HashScroll } from '@/components/layout/HashScroll'
import { SkipLink } from '@/components/layout/SkipLink'
import { OrganizationSchema } from '@/components/layout/OrganizationSchema'
import { LanguageProvider, STORAGE_KEY } from '@/i18n/LanguageContext'
import type { Lang } from '@/i18n/translations'
import { HomePage } from '@/pages/HomePage'
import { ViberBubble } from '@/components/ui/ViberBubble'
import { CookieConsent } from '@/components/ui/CookieConsent'
import { useCookieConsent } from '@/lib/useCookieConsent'

/**
 * Perf audit (Lighthouse, Moto G Power/slow 4G, Aug 2026) flagged ~146KB of
 * unused JS in the main bundle, largely `jspdf` + `html2canvas` (~110KB)
 * pulled in by FakturaPage — a hidden internal tool (see its own header
 * comment) that's not even reachable from the public site nav, yet every
 * visitor downloaded it on first load because every page was a static
 * top-level import here. Only HomePage stays a static import (it's the
 * route almost every visitor lands on first — lazy-loading it would trade
 * a smaller initial bundle for an extra network round-trip on the page
 * that matters most for FCP/LCP). Every other route, and FakturaPage
 * especially, is React.lazy'd instead so its JS (and jspdf/html2canvas by
 * extension) only downloads for visitors who actually navigate there.
 * scripts/prerender.ts already waits on `networkidle` + non-empty #root
 * before snapshotting, which it calls out as specifically there to handle
 * "slow-loading routes (e.g. code-split pages)" — so this doesn't break
 * prerendering.
 */
const BlogListPage = lazy(() => import('@/pages/BlogListPage').then((m) => ({ default: m.BlogListPage })))
const BlogArticlePage = lazy(() => import('@/pages/BlogArticlePage').then((m) => ({ default: m.BlogArticlePage })))
const KontaktPage = lazy(() => import('@/pages/KontaktPage').then((m) => ({ default: m.KontaktPage })))
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })))
const GraphicDesignPage = lazy(() => import('@/pages/GraphicDesignPage').then((m) => ({ default: m.GraphicDesignPage })))
const PrintMaterialsPage = lazy(() => import('@/pages/PrintMaterialsPage').then((m) => ({ default: m.PrintMaterialsPage })))
const WebCoveragePage = lazy(() => import('@/pages/WebCoveragePage').then((m) => ({ default: m.WebCoveragePage })))
const CjenovnikPage = lazy(() => import('@/pages/CjenovnikPage').then((m) => ({ default: m.CjenovnikPage })))
const TermsPage = lazy(() => import('@/pages/TermsPage').then((m) => ({ default: m.TermsPage })))
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })))
const FakturaPage = lazy(() => import('@/pages/FakturaPage').then((m) => ({ default: m.FakturaPage })))
const AboutPage = lazy(() => import('@/pages/AboutPage').then((m) => ({ default: m.AboutPage })))
const CaseStudyPage = lazy(() => import('@/pages/CaseStudyPage').then((m) => ({ default: m.CaseStudyPage })))

/**
 * Phase 7: language became a real URL segment (`/bs/...` / `/en/...`) so
 * real hreflang tags are possible (see useDocumentMeta.ts) — previously
 * both languages lived at the same URL via a client-side-only toggle,
 * which can't be hreflang'd at all. `/` redirects to whichever language
 * the visitor last used (their `ea-lang` localStorage value, same key the
 * old toggle wrote), defaulting to `/bs` for first-time visitors.
 */
function RootRedirect() {
  const stored = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null
  return <Navigate to={stored === 'en' ? '/en' : '/bs'} replace />
}

/** Validates the `:lang` route param and mounts the whole app under that language. Anything other than bs/en falls back to /bs. */
function LangRoot() {
  const { lang } = useParams<{ lang: string }>()
  // Single shared instance, passed down to both CookieConsent and
  // ViberBubble — see their header comments for why this can't just be
  // two independent hook calls (dismissing in one wouldn't tell the other).
  const { visible: cookieBannerVisible, dismiss: dismissCookieBanner } = useCookieConsent()

  if (lang !== 'bs' && lang !== 'en') return <Navigate to="/bs" replace />

  return (
    <LanguageProvider lang={lang as Lang}>
      <SkipLink />
      <HashScroll />
      <DesktopNav />
      <MobileNav />
      <Suspense fallback={null}>
        <Routes>
          <Route index element={<HomePage />} />
          <Route path="blog" element={<BlogListPage />} />
          <Route path="blog/:slug" element={<BlogArticlePage />} />
          <Route path="kontakt" element={<KontaktPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="graficki-dizajn" element={<GraphicDesignPage />} />
          <Route path="print-materijali" element={<PrintMaterialsPage />} />
          <Route path="izrada-web-stranica" element={<WebCoveragePage />} />
          <Route path="cjenovnik" element={<CjenovnikPage />} />
          <Route path="o-meni" element={<AboutPage />} />
          <Route path="portfolio/:slug" element={<CaseStudyPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      <SiteFooter />
      {/* Outside the page <Routes> but still inside the lang provider — persists across every page. */}
      <ViberBubble cookieBannerVisible={cookieBannerVisible} />
      <CookieConsent visible={cookieBannerVisible} dismiss={dismissCookieBanner} />
    </LanguageProvider>
  )
}

function App() {
  return (
    <>
      <OrganizationSchema />
      <Suspense fallback={null}>
        <Routes>
          {/* Hidden internal tool — no nav/footer, not under /:lang, not
              prerendered/sitemapped, disallowed in robots.txt. See
              handoff.md for why. */}
          <Route path="/faktura" element={<FakturaPage />} />
          <Route path="/" element={<RootRedirect />} />
          <Route path="/:lang/*" element={<LangRoot />} />
          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </Suspense>
    </>
  )
}

export default App
